/**
 * CHANGE CARD — THE COMPOSER'S CODEC (KPE-028, KPR-708). Pure.
 *
 * A target element of a card change is built in the composer: the formula
 * editor's pill editor, with free text, pills and transforms. The editor holds
 * one string, free text with `{{pill}}`s, and this module is the one place
 * that string becomes a FILL (the vendored `compose.js` says which shapes the
 * server accepts), so the composer never offers or sends what the server
 * refuses, and every refusal it makes locally reads in the server's sentence.
 *
 *   {{name}}                 a source element, written as a person reads it
 *   {{a.b}} … {{a.b.c.d}}    a dot-walk through item lookups (at most MAX_HOPS)
 *   {{title}} {{createdAt}} {{updatedAt}}   the item's own
 *   {{user.name}} {{user.email}} {{collection.name}}   dynamic values
 *   {{today()}} {{now()}}    the clock (today() is the UTC date, as everywhere)
 *   {{<base>|fn|arg|arg}}    a TRANSFORM: one function of the formula
 *                            language applied to the pill, its other
 *                            arguments literals, percent-encoded
 *   {{=<encoded value>}}     a FIXED value, in the target element's stored
 *                            shape (a non-text target only)
 *
 * A TEXT target (short, long, rich) takes any number of pills and text in any
 * order and sends `parts`. A non-text target takes exactly one pill: a plain
 * source element sends `element` (so the compatibility table, units, currency
 * and choice maps work as they do for a dropped pill), a fixed value sends
 * `static`, anything else sends `formula`.
 *
 * Which functions can be a transform comes from the formula module's typed
 * parameters (`describeFunction`), never from a list written here: a function
 * whose first parameter takes the pill's type and whose other parameters are
 * literals (a number, text, or a value picked from a list).
 */
import { checkFill, pathOf, takesParts, MAX_PARTS, MAX_HOPS } from '../../utils/changeCard/compose';
import { describeElement, describeFunction, describeFunctions, evaluate, FormulaError, parse, renderText, analyze, formulaUnits, formulaCurrency } from '../../utils/formula';
import { parseTemplate } from '../common/tokenTemplate';
import { normalizeId } from '../../utils/ids';

export { MAX_HOPS, MAX_PARTS };

/** The depth of the menu's walks: the server takes three lookups, the menu offers two; a longer one is typed. */
export const MENU_HOPS = 2;
const MENU_BUDGET = 500;

const hasOwn = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);

/** Types a formula never reads as a value: their pill is not offered. */
const NEVER_READ = new Set(['file', 'confirmations', 'repeating-group']);
/** Types whose formula value is an id, which the server never writes as text: no transform. */
const ID_TYPES = new Set(['card-lookup', 'user']);

const labelOf = el => {
	const label = el?.label;
	const text = label && typeof label === 'object' ? label.singular : label;
	return typeof text === 'string' && text.trim() ? text.trim() : el?.name || '';
};

// ------------------------------------------------------------------ tokens

const encode = text => encodeURIComponent(String(text));
const decode = text => {
	try {
		return decodeURIComponent(text);
	} catch (err) {
		return text;
	}
};

/** The token for a pill: `{{base}}`, `{{base|fn|arg…}}`; trailing empty arguments are dropped. */
export function tokenOf({ base, fn = null, args = [] }) {
	if (!base) return '';
	if (!fn) return `{{${base}}}`;
	const list = [...args];
	while (list.length && !String(list[list.length - 1] ?? '').trim()) list.pop();
	return `{{${[base, fn, ...list.map(arg => encode(arg ?? ''))].join('|')}}}`;
}

/** The token for a fixed value (any JSON the element stores). */
export function staticToken(value) {
	return `{{=${encode(JSON.stringify(value === undefined ? null : value))}}}`;
}

/**
 * One token read back: `{ token, base, fn, args }`, or `{ token, fixed: true,
 * value }`, or `{ token, broken: true }` for a `{{=…}}` that does not decode.
 */
export function readToken(token) {
	const inner = String(token).slice(2, -2).trim();
	if (inner.startsWith('=')) {
		try {
			return { token, fixed: true, value: JSON.parse(decode(inner.slice(1))) };
		} catch (err) {
			return { token, broken: true };
		}
	}
	const parts = inner.split('|');
	return {
		token,
		base: parts[0].trim(),
		fn: parts[1] ? parts[1].trim() : null,
		args: parts.slice(2).map(decode),
	};
}

/** The composer's text as segments: `{ type: 'text', text }` and `{ type: 'pill', ...readToken }`, in order. */
export function readComposer(text) {
	return parseTemplate(typeof text === 'string' ? text : '').map(segment => (
		segment.type === 'token' ? { type: 'pill', ...readToken(segment.token) } : { type: 'text', text: segment.text }
	));
}

const isCall = base => /\(\)$/.test(base || '');
const CLOCK = ['today()', 'now()'];
const isClock = base => CLOCK.includes(base);

/** A pill's base as formula source: `{{a.b}}`, or a call as it is. */
const baseExpr = base => (isCall(base) ? base : `{{${base}}}`);

/** A literal argument as formula source: numbers as typed, text and picked values quoted. */
function argSource(param, raw) {
	const value = String(raw ?? '').trim();
	if (param.kind === 'number') return value;
	return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/** The formula a pill stands for. Throws nothing; a missing required argument is `problem`. */
export function pillFormula(pill) {
	if (pill.fixed || pill.broken) return { problem: 'A fixed value is not a formula.' };
	if (!pill.fn) return { formula: baseExpr(pill.base) };
	const described = describeFunction(pill.fn, { units: formulaUnits });
	if (!described) return { problem: `${pill.fn} is not a function of the formula language.` };
	const extra = described.params.slice(1);
	const out = [baseExpr(pill.base)];
	for (let i = 0; i < extra.length; i++) {
		const raw = pill.args[i];
		const given = raw !== undefined && String(raw).trim() !== '';
		if (!given) {
			if (extra[i].optional) break;
			return { problem: `${pill.fn} needs ${extra[i].name}.` };
		}
		out.push(argSource(extra[i], raw));
	}
	return { formula: `${pill.fn}(${out.join(', ')})` };
}

// ---------------------------------------------------------------- the pill's type

const WALK_TERMINAL_TYPES = { title: 'text', createdAt: 'datetime', updatedAt: 'datetime' };

/**
 * What a pill is, for the transform it may take: `{ type, measure?, dataType?,
 * element? }`, or null when it cannot be told (an unknown name, a walk the
 * cards do not show). `type` is the formula language's own (text, number, date,
 * datetime, time, bool, measure, money, lookup).
 */
export function pillType(base, { sourcesByName = {}, getElementsForCard = null } = {}) {
	if (!base) return null;
	if (base === 'today()') return { type: 'date' };
	if (base === 'now()') return { type: 'datetime' };
	if (hasOwn(sourcesByName, base)) {
		const el = sourcesByName[base];
		const d = describeElement(el);
		return { type: d.type, measure: d.measure || null, dataType: el.dataType || 'text-small', element: el };
	}
	const path = base.split('.');
	if (path.length === 1) return hasOwn(WALK_TERMINAL_TYPES, base) ? { type: WALK_TERMINAL_TYPES[base] } : null;
	if (path[0] === 'user' || path[0] === 'collection') return path.length === 2 ? { type: 'text' } : null;
	let el = hasOwn(sourcesByName, path[0]) ? sourcesByName[path[0]] : null;
	for (let i = 1; el && i < path.length; i++) {
		if (el.dataType !== 'card-lookup') return null;
		const last = path[i];
		if (i === path.length - 1 && hasOwn(WALK_TERMINAL_TYPES, last)) {
			const inner = (typeof getElementsForCard === 'function' ? getElementsForCard(normalizeId(el.options?.lookupCardId)) : []) || [];
			if (!inner.some(candidate => candidate?.name === last)) return { type: WALK_TERMINAL_TYPES[last] };
		}
		const elements = typeof getElementsForCard === 'function' ? (getElementsForCard(normalizeId(el.options?.lookupCardId)) || []) : [];
		el = elements.find(candidate => candidate?.name === last) || null;
	}
	if (!el) return null;
	const d = describeElement(el);
	return { type: d.type, measure: d.measure || null, dataType: el.dataType || 'text-small', element: el };
}

// ---------------------------------------------------------------- transforms

/** A function's literal parameters (everything after the pill): null when one is not a literal. */
function literalParams(described) {
	const rest = described.params.slice(1);
	if (rest.some(param => param.repeat)) return null;
	const out = [];
	for (let i = 0; i < rest.length; i++) {
		const param = rest[i];
		if (['number', 'text', 'choice', 'unit'].includes(param.kind)) out.push(param);
		else if (param.optional) break; // an optional element is simply left out
		else return null;
	}
	return out;
}

/**
 * The transforms a pill of `type` may take, grouped as the formula guide
 * groups its functions: `[{ group, options: [{ name, label, description,
 * example, params }] }]`. `params` are the literals to ask for, each `{ name,
 * kind, optional, choices? }`; a unit list is narrowed to the pill's measure.
 * A pill that holds an id (a lookup, a person) takes none.
 */
export function transformOptions(info) {
	if (!info || !info.type || ID_TYPES.has(info.dataType) || info.type === 'lookup' || info.type === 'list') return [];
	const groups = new Map();
	describeFunctions({ units: formulaUnits }).forEach(fn => {
		const first = fn.params[0];
		if (!first || first.repeat) return;
		if (!first.accepts.includes('any') && !first.accepts.includes(info.type)) return;
		const params = literalParams(fn);
		if (!params) return;
		// A function that takes no pill at all (today, now, blank) is not a transform.
		if (fn.params.length < 1) return;
		const shaped = params.map(param => {
			let choices = param.choices;
			if (Array.isArray(choices) && info.measure && param.measureOf !== undefined) {
				const narrowed = choices.filter(choice => choice.measure === info.measure);
				if (narrowed.length) choices = narrowed;
			}
			return { name: param.name, kind: param.kind, optional: !!param.optional, choices: Array.isArray(choices) ? choices : null };
		});
		if (!groups.has(fn.group)) groups.set(fn.group, []);
		groups.get(fn.group).push({ name: fn.name, label: fn.name, description: fn.description, example: fn.example, params: shaped });
	});
	return [...groups.entries()].map(([group, options]) => ({ group, options }));
}

/** One transform's description by name, or null (from the pill's own options). */
export function transformNamed(info, name) {
	for (const group of transformOptions(info)) {
		const found = group.options.find(option => option.name === name);
		if (found) return found;
	}
	return null;
}

// ---------------------------------------------------------------- examples

/**
 * The pill's value for a real item, as the formula language works it out:
 * `{ text }` or `{ none: true }` (the item holds nothing, or the pill reads
 * something this page does not have: a walk or the person). `values` are the
 * item's source values (the dry run's `before`); `sources` the source card's
 * elements. Never throws.
 */
export function exampleFor(pill, { sources = [], values = {}, now = new Date(), getElementsForCard = null } = {}) {
	const made = pillFormula(pill);
	if (!made.formula) return { none: true };
	try {
		const byName = new Map(sources.filter(el => el?.name).map(el => [el.name, el]));
		const ast = parse(made.formula);
		const describe = path => {
			if (path.length !== 1) return null;
			if (byName.has(path[0])) return describeElement(byName.get(path[0]));
			if (hasOwn(WALK_TERMINAL_TYPES, path[0])) return { type: WALK_TERMINAL_TYPES[path[0]] };
			return null;
		};
		analyze(ast, { resolveRef: describe, units: formulaUnits, currency: formulaCurrency });
		const read = path => (path.length === 1 ? (hasOwn(values, path[0]) ? values[path[0]] : undefined) : undefined);
		if (!pill.fn && !isClock(pill.base) && path1(pill.base) && !hasOwn(values, pill.base)) return { none: true };
		const { value } = evaluate(ast, { read, describe, units: formulaUnits, currency: formulaCurrency, now, blanks: 'empty' });
		if (value === null || value === undefined) return { none: true };
		const text = renderText(value, { units: formulaUnits, currency: formulaCurrency });
		return text === '' ? { none: true } : { text };
	} catch (err) {
		if (err instanceof FormulaError || err?.isFormulaError) return { none: true };
		return { none: true };
	}
}

const path1 = base => !!base && !base.includes('.') && !isCall(base);

// ---------------------------------------------------------------- the fill

/** A source element named by a plain pill (no transform, no walk, no clock): its name, or null. */
export function plainPill(pill, sourceNames) {
	if (!pill || pill.fixed || pill.broken || pill.fn || !pill.base) return null;
	return sourceNames.has(pill.base) ? pill.base : null;
}

const pillsOf = segments => segments.filter(segment => segment.type === 'pill');

/** The names of the source elements a composer text reads (a walk reads its first element). */
export function sourcesRead(text, sourceNames) {
	const names = sourceNames instanceof Set ? sourceNames : new Set(sourceNames || []);
	const out = [];
	pillsOf(readComposer(text)).forEach(pill => {
		if (pill.fixed || pill.broken || !pill.base) return;
		const head = pill.base.split('.')[0];
		if (names.has(head) && !out.includes(head)) out.push(head);
	});
	return out;
}

/** The source element a composer text is exactly one plain pill of, else null. */
export function plainSourceOf(text, sourceNames) {
	const names = sourceNames instanceof Set ? sourceNames : new Set(sourceNames || []);
	const segments = readComposer(text).filter(segment => segment.type !== 'text' || segment.text.trim() !== '');
	if (segments.length !== 1 || segments[0].type !== 'pill') return null;
	return plainPill(segments[0], names);
}

/** Is this pill one the composer may keep? Returns the sentence when not. */
function pillProblem(pill, sourceNames) {
	if (pill.broken) return 'A fixed value could not be read. Remove it and set it again.';
	if (pill.fixed) return null;
	if (isCall(pill.base)) return isClock(pill.base) ? null : `${pill.base} is not a value the card change offers.`;
	const checked = pathOf(pill.base.split('.'), sourceNames);
	if (checked.problem) return checked.problem.message;
	return null;
}

/**
 * The fill a composer text stands for on `target`.
 *   { empty: true }      nothing to send: the text is blank
 *   { problem }          one sentence; nothing is sent while it stands
 *   { fill }             the fill, in the server's shape
 * `mapping` is what a plain element pill carries (choices, unit, currency).
 */
export function composerFill(text, target, { sourceNames = [], mapping = null } = {}) {
	const names = sourceNames instanceof Set ? sourceNames : new Set(sourceNames);
	const label = labelOf(target) || 'This element';
	const all = readComposer(text);
	const textual = takesParts(target);
	const segments = textual ? all : all.filter(segment => segment.type !== 'text' || segment.text.trim() !== '');
	if (!segments.length || (!pillsOf(segments).length && !segments.some(segment => segment.text.trim() !== ''))) return { empty: true };

	for (const pill of pillsOf(segments)) {
		const problem = pillProblem(pill, names);
		if (problem) return { problem: `${label}: ${problem}` };
	}

	if (!textual) {
		const pills = pillsOf(segments);
		if (segments.length !== 1 || pills.length !== 1) {
			return { problem: `${label} holds one value, so it takes one pill and no text. Use one element, one fixed value, or one transform.` };
		}
		const pill = pills[0];
		if (pill.fixed) {
			const checked = checkFill({ kind: 'static', value: pill.value }, target);
			return checked.problem ? { problem: checked.problem.message } : { fill: checked.fill };
		}
		const plain = plainPill(pill, names);
		if (plain) {
			const carried = {};
			if (mapping && mapping.choices) carried.choices = mapping.choices;
			if (mapping && mapping.unit) carried.unit = mapping.unit;
			if (mapping && mapping.currency) carried.currency = mapping.currency;
			return { fill: Object.keys(carried).length ? { kind: 'element', element: plain, mapping: carried } : { kind: 'element', element: plain } };
		}
		const made = pillFormula(pill);
		if (made.problem) return { problem: `${label}: ${made.problem}` };
		const checked = checkFill({ kind: 'formula', formula: made.formula }, target);
		return checked.problem ? { problem: checked.problem.message } : { fill: checked.fill };
	}

	// A text: parts, in order.
	const pills = pillsOf(segments);
	if (pills.some(pill => pill.fixed)) {
		return { problem: `${label} is text: type the words instead of adding a fixed value.` };
	}
	if (segments.length === 1 && pills.length === 1) {
		const plain = plainPill(pills[0], names);
		if (plain) return { fill: { kind: 'element', element: plain } };
	}
	const parts = [];
	for (const segment of segments) {
		if (segment.type === 'text') {
			if (segment.text) parts.push({ text: segment.text });
			continue;
		}
		const plain = plainPill(segment, names);
		if (plain) {
			parts.push({ element: plain });
			continue;
		}
		const made = pillFormula(segment);
		if (made.problem) return { problem: `${label}: ${made.problem}` };
		parts.push({ formula: made.formula });
	}
	if (!parts.length) return { empty: true };
	const checked = checkFill({ kind: 'parts', parts }, target);
	return checked.problem ? { problem: checked.problem.message } : { fill: checked.fill };
}

// ---------------------------------------------------------------- the vocabulary

/**
 * The pill menu for a composer, the shape the formula editor's menu takes:
 * source elements (a lookup with its walks, two hops deep and never through a
 * card twice on one path), the item's own, the clock, the person, the
 * collection. Hidden source elements are not here: `sources` are what
 * `sourcesOf` allows. A file, a repeating group and a "Me too" count are not
 * values a formula reads and are not offered.
 */
export function composerTokens(sources, { getElementsForCard = null, target = null } = {}) {
	const budget = { left: MENU_BUDGET };
	const readable = els => (els || []).filter(el => el?.name && !NEVER_READ.has(el.dataType));
	const walk = (lookup, path, labels, hops, seen) => {
		const cardId = normalizeId(lookup?.options?.lookupCardId);
		if (hops >= MENU_HOPS || !cardId || seen.has(cardId) || typeof getElementsForCard !== 'function') return [];
		const next = new Set(seen).add(cardId);
		const here = [];
		const title = { token: `{{${[...path, 'title'].join('.')}}}`, label: `${[...labels, 'Title'].join(' · ')}`, menuLabel: 'Title of the linked item' };
		here.push(title);
		readable(getElementsForCard(cardId)).forEach(child => {
			if (budget.left <= 0) return;
			budget.left -= 1;
			const childPath = [...path, child.name];
			const childLabels = [...labels, labelOf(child)];
			const node = { token: `{{${childPath.join('.')}}}`, label: childLabels.join(' · '), menuLabel: labelOf(child) };
			if (child.dataType === 'card-lookup') {
				const inner = walk(child, childPath, childLabels, hops + 1, next);
				if (inner.length) node.children = [{ ...node, menuLabel: `${labelOf(child)} (the link itself)` }, ...inner];
			}
			here.push(node);
		});
		return here;
	};
	const own = readable(sources).map(el => {
		const node = { token: `{{${el.name}}}`, label: labelOf(el), menuLabel: labelOf(el) };
		if (el.dataType !== 'card-lookup') return node;
		const children = walk(el, [el.name], [labelOf(el)], 0, new Set());
		return children.length ? { ...node, children: [{ ...node, menuLabel: `${labelOf(el)} (the link itself)` }, ...children] } : node;
	});
	const you = [
		{ token: '{{user.name}}', label: 'Your name', menuLabel: 'Your name' },
		{ token: '{{user.email}}', label: 'Your email', menuLabel: 'Your email address' },
	];
	if (target && target.dataType === 'user') you.push({ token: '{{user.id}}', label: 'You', menuLabel: 'You (the person changing the card)' });
	return [
		...own,
		{ label: 'This item', children: [
			{ token: '{{title}}', label: 'Title', menuLabel: 'Title' },
			{ token: '{{createdAt}}', label: 'Created', menuLabel: 'Created' },
			{ token: '{{updatedAt}}', label: 'Updated', menuLabel: 'Updated' },
		] },
		{ label: 'Today and now', children: [
			{ token: '{{today()}}', label: 'Today', menuLabel: 'Today' },
			{ token: '{{now()}}', label: 'Now', menuLabel: 'Now' },
		] },
		{ label: 'You', children: you },
		{ label: 'This collection', children: [{ token: '{{collection.name}}', label: 'Collection name', menuLabel: 'Collection name' }] },
	];
}

/**
 * The words on a pill. A transform is added to the pill's own words:
 * "Price · round 2". `labelOf(base)` answers for an untransformed base.
 */
export function pillWords(token, baseLabel, fixedLabel) {
	const pill = readToken(token);
	if (pill.broken) return 'Fixed value';
	if (pill.fixed) return fixedLabel(pill.value);
	const base = baseLabel(pill.base);
	if (!pill.fn) return base;
	const args = pill.args.filter(arg => String(arg).trim() !== '').join(', ');
	return `${base} · ${pill.fn}${args ? ` ${args}` : ''}`;
}

/** The text with one more pill at the end. */
export function appendPill(text, token) {
	return `${typeof text === 'string' ? text : ''}${token}`;
}

/** Re-exported for the callers that need the cap in words. */
export { takesParts };
